'use client';

import { useEffect, useRef, useState } from 'react';
import type { SellVsRentBlockProps } from '../types';
import { theme, sectionColors, buttonVars } from '../theme';
import { CALC_SMS_CONSENT_TEXT, FIELD, NumberField, PRIMARY_BTN, PdfGate, money, numberOrUndefined, pageUrl, readBrowserId, readValuationToken } from './calcShared';

/**
 * Sell-vs-rent calculator — V3.1 seller calculators (docs/V3_LAUNCH_PLAN.md §4). CLIENT block,
 * framework-agnostic:
 *
 *   1. inputs (value today, achievable rent, payoff + mortgage payment, taxes/insurance/HOA,
 *      horizon, appreciation, return on invested proceeds) → POST same-origin /api/calc/sell-vs-rent;
 *      the gateway runs the comparison and records `sell_vs_rent_run` when the browser is known.
 *      "Estimate rent from nearby rentals" → GET /api/calc/rent-estimate (median asking rent of the
 *      feed's active rentals within 2 miles; needs an address).
 *   2. results: year-1 cash flow, cash-on-equity, HOLD vs SELL after N years, the verdict, a
 *      year-by-year table.
 *   3. the gate ("Get the itemized breakdown (PDF)") — same PdfGate as net-proceeds.
 *
 * `?valuation=<token>` prefills today's value on mount through the same endpoint with
 * `prefill_only: true` — a READ: the gateway answers the valuation-derived value/state/address and
 * records no run, no event and no contact attributes. The comparison itself only runs when the
 * visitor submits a rent. "Estimate rent" writes into the rent field alone (a ref), so nothing the
 * visitor has typed elsewhere in the form is lost.
 */

interface Year { year: number; grossRent: number; netRent: number; cashFlow: number; homeValue: number; holdTotal: number; sellTotal: number }
interface Outputs {
  assumptions: { currentValue: number; monthlyRent: number; payoff: number; years: number; appreciationPct: number; rentGrowthPct: number; conservativeReturnPct: number; vacancyPct: number; maintenancePct: number };
  annualGrossRent: number; annualVacancy: number; annualMaintenance: number; annualCarrying: number; annualNetRent: number; annualMortgage: number; annualCashFlow: number;
  equity: number; cashOnEquityYield: number | null; netProceedsNow: number; sellNowFutureValue: number; holdFutureValue: number; difference: number;
  verdict: 'hold' | 'sell' | 'close'; yearly: Year[];
}
interface Prefill { current_value: number; state: string | null; address: string }
interface RunResponse {
  data?: { run_id: string | null; outputs: Outputs | null; prefill: Prefill | null } | null;
  error?: string | null;
}
interface RentResponse { data?: { rent: number | null; sample_size: number; radius_miles: number | null } | null; error?: string | null }

export function SellVsRentBlock(props: SellVsRentBlockProps & { turnstileSiteKey?: string | null }) {
  const {
    heading = 'Sell now, or keep it and rent?',
    subheading = 'Compare what selling today puts in your pocket with what holding the home as a rental could earn.',
    buttonText = 'Compare',
    defaultState = '',
    appreciationPct = 3,
    conservativeReturnPct = 4,
    years = 5,
    showPhone = true,
    phoneRequired = false,
    consentText = CALC_SMS_CONSENT_TEXT,
  } = props;
  // The section's button colours ride on the section as --blk-btn vars (plan E6).
  const colors = { ...sectionColors(props, { bg: 'transparent', text: theme.text }), ...buttonVars(props) };

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ runId: string | null; outputs: Outputs } | null>(null);
  const [prefill, setPrefill] = useState<{ current_value?: number; state?: string; address?: string }>({});
  const [rentNote, setRentNote] = useState<string | null>(null);
  const [rentPending, setRentPending] = useState(false);
  const rentRef = useRef<HTMLInputElement>(null);

  const applyPrefill = (p: Prefill) => setPrefill((prev) => ({ ...prev, current_value: p.current_value, state: p.state ?? undefined, address: p.address }));

  async function run(inputs: Record<string, unknown>, valuationToken?: string) {
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/calc/sell-vs-rent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...inputs, valuation_token: valuationToken, browser_id: readBrowserId(), page_url: pageUrl() }),
      });
      const body = (await res.json().catch(() => ({}))) as RunResponse;
      if (!res.ok || !body.data?.outputs) throw new Error(body.error || `Request failed: ${res.status}`);
      setResult({ runId: body.data.run_id, outputs: body.data.outputs });
      if (body.data.prefill) applyPrefill(body.data.prefill);
    } catch (err) {
      setError(err instanceof Error && err.message && !/Request failed/.test(err.message) ? err.message : 'Sorry, we could not run the comparison. Please check the inputs and try again.');
    } finally {
      setPending(false);
    }
  }

  /** The read-only prefill from the visitor's value report: no run, no event, no attributes recorded. */
  async function prefillFromValuation(token: string): Promise<void> {
    try {
      const res = await fetch('/api/calc/sell-vs-rent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prefill_only: true, valuation_token: token }),
      });
      const body = (await res.json().catch(() => ({}))) as RunResponse;
      if (res.ok && body.data?.prefill) applyPrefill(body.data.prefill);
    } catch {
      /* the visitor types the value */
    }
  }

  async function estimateRent(address: string, sqft: number | undefined): Promise<number | null> {
    setRentPending(true);
    setRentNote(null);
    try {
      const qs = new URLSearchParams({ address });
      if (sqft) qs.set('sqft', String(sqft));
      const res = await fetch(`/api/calc/rent-estimate?${qs.toString()}`);
      const body = (await res.json().catch(() => ({}))) as RentResponse;
      const rent = body.data?.rent ?? null;
      if (rent) {
        setRentNote(`Median asking rent of ${body.data?.sample_size} active rentals within ${body.data?.radius_miles ?? 2} miles.`);
        // Only the rent field changes — the form is NOT remounted, so typed values survive.
        if (rentRef.current) rentRef.current.value = String(rent);
        return rent;
      }
      setRentNote('Not enough nearby rentals in the feed to estimate — enter what you think the home would rent for.');
      return null;
    } catch {
      setRentNote('Could not look up nearby rentals right now — enter your own rent figure.');
      return null;
    } finally {
      setRentPending(false);
    }
  }

  // From the value report: prefill today's value (and try the rent estimate for the report's address).
  useEffect(() => {
    const token = readValuationToken();
    if (token) void prefillFromValuation(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prefill.address && !rentPending && !rentNote) void estimateRent(prefill.address, undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill.address]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const currentValue = numberOrUndefined(fd.get('current_value'));
    const monthlyRent = numberOrUndefined(fd.get('monthly_rent'));
    if (!currentValue || currentValue <= 0) { setError("Please enter what the home is worth today."); return; }
    if (monthlyRent === undefined || monthlyRent < 0) { setError('Please enter the monthly rent the home could bring in.'); return; }
    void run({
      current_value: currentValue,
      monthly_rent: monthlyRent,
      state: String(fd.get('state') ?? '').trim().toUpperCase() || undefined,
      payoff: numberOrUndefined(fd.get('payoff')),
      monthly_mortgage: numberOrUndefined(fd.get('monthly_mortgage')),
      monthly_taxes: numberOrUndefined(fd.get('monthly_taxes')),
      monthly_insurance: numberOrUndefined(fd.get('monthly_insurance')),
      monthly_hoa: numberOrUndefined(fd.get('monthly_hoa')),
      years: numberOrUndefined(fd.get('years')),
      appreciation_pct: numberOrUndefined(fd.get('appreciation_pct')),
      conservative_return_pct: numberOrUndefined(fd.get('conservative_return_pct')),
    });
  }

  function handleRentLookup(e: React.MouseEvent<HTMLButtonElement>) {
    const form = e.currentTarget.form;
    if (!form) return;
    const fd = new FormData(form);
    const address = String(fd.get('address') ?? '').trim();
    if (address.length < 5) { setRentNote('Enter the home’s address to look up nearby rentals.'); return; }
    void estimateRent(address, numberOrUndefined(fd.get('sqft')));
  }

  const out = result?.outputs ?? null;
  const verdictLine = out
    ? out.verdict === 'close'
      ? 'Roughly even — the choice comes down to your plans, not the maths.'
      : out.verdict === 'hold'
        ? `Keeping and renting comes out ahead by about ${money(Math.abs(out.difference))} over ${out.assumptions.years} years.`
        : `Selling now comes out ahead by about ${money(Math.abs(out.difference))} over ${out.assumptions.years} years.`
    : null;

  return (
    <section id="sell-vs-rent" className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }}>
      <div className="mx-auto max-w-3xl">
        <h2 className="mb-2 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {subheading ? <p className="mb-6 opacity-80">{subheading}</p> : null}
        {error ? <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p> : null}

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <form onSubmit={handleSubmit} className="space-y-4" data-calc="sell-vs-rent-form" key={`${prefill.current_value ?? ''}-${prefill.state ?? ''}`}>
            <NumberField name="current_value" label="What the home is worth today" prefix="$" step={1000} defaultValue={prefill.current_value} placeholder="600,000" required />
            <label htmlFor="calc-address" className="block text-sm">
              <span className="mb-1 block font-medium">Address (for a rent estimate)</span>
              <span className="flex gap-2">
                <input id="calc-address" name="address" type="text" defaultValue={prefill.address ?? ''} placeholder="123 Ocean Ave, City" className={FIELD} autoComplete="street-address" />
                <button type="button" onClick={handleRentLookup} disabled={rentPending} className="whitespace-nowrap rounded-md border border-black/15 px-3 text-sm hover:bg-black/5 disabled:opacity-60">
                  {rentPending ? 'Looking…' : 'Estimate rent'}
                </button>
              </span>
              {rentNote ? <span className="mt-1 block text-xs opacity-70">{rentNote}</span> : null}
            </label>
            <NumberField name="sqft" label="Square feet (optional, sharpens the rent estimate)" placeholder="2,000" />
            <NumberField name="monthly_rent" label="Achievable monthly rent" prefix="$" step={50} placeholder="3,400" required inputRef={rentRef} />
            <label htmlFor="calc-state" className="block text-sm">
              <span className="mb-1 block font-medium">State</span>
              <input id="calc-state" name="state" type="text" maxLength={2} placeholder="FL" defaultValue={prefill.state ?? defaultState} className={`${FIELD} uppercase`} autoComplete="address-level1" />
            </label>
            <NumberField name="payoff" label="Mortgage payoff" prefix="$" step={1000} placeholder="0" />
            <NumberField name="monthly_mortgage" label="Monthly mortgage payment (P&I)" prefix="$" step={50} placeholder="0" help="The payment that continues if you keep the home." />
            <div className="grid grid-cols-3 gap-3">
              <NumberField name="monthly_taxes" label="Taxes / mo" prefix="$" step={25} placeholder="0" />
              <NumberField name="monthly_insurance" label="Insurance / mo" prefix="$" step={25} placeholder="0" />
              <NumberField name="monthly_hoa" label="HOA / mo" prefix="$" step={25} placeholder="0" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <NumberField name="years" label="Years" defaultValue={years} min={1} />
              <NumberField name="appreciation_pct" label="Appreciation" suffix="%" step={0.5} min={-20} defaultValue={appreciationPct} help="Can be negative." />
              <NumberField name="conservative_return_pct" label="Invested return" suffix="%" step={0.5} defaultValue={conservativeReturnPct} />
            </div>
            <button type="submit" disabled={pending} className={`blk-btn ${PRIMARY_BTN}`}>
              {pending ? 'Comparing…' : out ? 'Update the comparison' : buttonText}
            </button>
          </form>

          <div>
            {out ? (
              <div className="rounded-lg border border-black/10 p-5" data-calc="sell-vs-rent-result">
                <p className="text-xs uppercase tracking-wide opacity-70">After {out.assumptions.years} {out.assumptions.years === 1 ? 'year' : 'years'}</p>
                <p className="mt-2 text-lg font-semibold" style={{ fontFamily: theme.headingFont }} data-verdict={out.verdict}>{verdictLine}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-md bg-black/5 p-3">
                    <p className="text-xs uppercase tracking-wide opacity-70">Sell now, invest</p>
                    <p className="mt-1 text-xl font-semibold">{money(out.sellNowFutureValue)}</p>
                    <p className="text-xs opacity-70">{money(out.netProceedsNow)} net today at {out.assumptions.conservativeReturnPct}%</p>
                  </div>
                  <div className="rounded-md bg-black/5 p-3">
                    <p className="text-xs uppercase tracking-wide opacity-70">Keep, rent, sell later</p>
                    <p className="mt-1 text-xl font-semibold">{money(out.holdFutureValue)}</p>
                    <p className="text-xs opacity-70">{out.assumptions.appreciationPct}% appreciation + rent reinvested</p>
                  </div>
                </div>
                <table className="mt-4 w-full text-sm">
                  <tbody>
                    <tr className="border-t border-black/10"><td className="py-2 pr-3">Gross rent, year 1</td><td className="py-2 text-right">{money(out.annualGrossRent)}</td></tr>
                    <tr className="border-t border-black/10"><td className="py-2 pr-3">Vacancy ({out.assumptions.vacancyPct}%) + maintenance ({out.assumptions.maintenancePct}%)</td><td className="py-2 text-right">− {money(out.annualVacancy + out.annualMaintenance)}</td></tr>
                    <tr className="border-t border-black/10"><td className="py-2 pr-3">Taxes, insurance, HOA</td><td className="py-2 text-right">− {money(out.annualCarrying)}</td></tr>
                    <tr className="border-t border-black/20 font-medium"><td className="py-2 pr-3">Net rent before the mortgage</td><td className="py-2 text-right">{money(out.annualNetRent)}</td></tr>
                    <tr className="border-t border-black/10"><td className="py-2 pr-3">Mortgage payments</td><td className="py-2 text-right">− {money(out.annualMortgage)}</td></tr>
                    <tr className="border-t border-black/20 font-semibold"><td className="py-2 pr-3">Cash flow, year 1</td><td className="py-2 text-right">{money(out.annualCashFlow)}</td></tr>
                    {out.cashOnEquityYield !== null ? (
                      <tr className="border-t border-black/10"><td className="py-2 pr-3">Return on the {money(out.equity)} of equity you leave in</td><td className="py-2 text-right">{out.cashOnEquityYield}%</td></tr>
                    ) : null}
                  </tbody>
                </table>
                <details className="mt-3 text-sm">
                  <summary className="cursor-pointer opacity-80">Year by year</summary>
                  <table className="mt-2 w-full text-xs">
                    <thead><tr className="text-left opacity-70"><th className="py-1">Yr</th><th className="py-1 text-right">Cash flow</th><th className="py-1 text-right">Home value</th><th className="py-1 text-right">Keep</th><th className="py-1 text-right">Sell now</th></tr></thead>
                    <tbody>
                      {out.yearly.map((y) => (
                        <tr key={y.year} className="border-t border-black/10"><td className="py-1">{y.year}</td><td className="py-1 text-right">{money(y.cashFlow)}</td><td className="py-1 text-right">{money(y.homeValue)}</td><td className="py-1 text-right">{money(y.holdTotal)}</td><td className="py-1 text-right">{money(y.sellTotal)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </details>
                <p className="mt-3 text-xs opacity-60">
                  Assumes {out.assumptions.rentGrowthPct}% yearly rent growth, the same payoff at the later sale, and typical closing costs both times. An estimate, not tax or investment advice.
                </p>
                {result?.runId ? (
                  <PdfGate kind="sell_vs_rent" runId={result.runId} showPhone={showPhone} phoneRequired={phoneRequired} consentText={consentText} turnstileSiteKey={props.turnstileSiteKey} />
                ) : null}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-black/15 p-5 text-sm opacity-70" data-calc="sell-vs-rent-empty">
                Your comparison — year-one cash flow, the return on your equity, and where each path lands after {years} years — appears here.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
